import { useContext, useEffect, useState, useCallback, useRef } from 'preact/hooks';
import type { Locale } from '@frame/protocol';
import { RuntimeContext, WidgetContext, type Mode, type RuntimeApi, type WidgetSize } from './context.ts';
import type { MessageCatalog, Messages } from '../shared.ts';

// Hooks for widget components (client.tsx). They only work inside a widget rendered by the runtime.

/** The full runtime API (RuntimeApi): playAudio, toast, openApp, holdAwake… */
export const useRuntime = (): RuntimeApi => useContext(RuntimeContext);
/** The widget instance being rendered: { id, module, type, settings, size }. */
export const useWidget = () => useContext(WidgetContext);

/** The widget's settings from the panel (parsed with its zod schema, so defaults are filled in). */
export function useSettings<T = Record<string, any>>(): T {
  return useContext(WidgetContext).settings as T;
}

/**
 * The widget's box on the launcher grid; undefined in other zones. Re-renders when the grid or the widget's size changes.
 * `{ w, h }` are cells; `width`/`height` are in DESIGN pixels (the space your CSS px live in: the runtime scales the whole widget to the
 * real grid, so a 1x1 is always 122 design px), `scale` is the real/design ratio and `shape` is 'dot' | 'bar' | 'card'.
 * Write CSS in px for that design space, switch layouts on `shape`/`width`/`height` (not on a fixed cell size), use `em`/`FitText`
 * for text, and never depend on real screen pixels. Content that still overflows the box is shrunk to fit by the runtime.
 */
export function useSize(): WidgetSize | undefined {
  return useContext(WidgetContext).size;
}

/**
 * Call one of the module's server actions: const call = useAction(); await call('setLevel', { level: 0 }).
 * The action runs with ctx.widget set to this widget; the promise rejects with the action's error message.
 */
export function useAction() {
  const rt = useRuntime();
  const w = useWidget();
  return useCallback((name: string, input?: unknown) => rt.action(w.id, name, input), [rt, w.id]);
}

/**
 * Live data the server sent with ctx.push(channel, data); re-renders on every push. Defaults to the
 * widget's own module. undefined until the first value arrives (the last value is kept across reconnects).
 */
export function useChannel<T = unknown>(channel: string, module?: string): T | undefined {
  const rt = useRuntime();
  const w = useWidget();
  const mod = module || w.module;
  const [value, setValue] = useState(() => rt.getChannel(mod, channel) as T | undefined);
  useEffect(() => rt.subscribe(mod, channel, (v) => setValue(v as T)), [rt, mod, channel]);
  return value;
}

/** 'idle' (gallery / art mode) or 'active' (launcher). */
export function useMode(): Mode {
  const rt = useRuntime();
  const [mode, setMode] = useState(rt.mode());
  useEffect(() => rt.onMode(setMode), [rt]);
  return mode;
}

/**
 * Language of the TV and all apps on it ('en' | 'tr'); re-renders when it's changed in the panel.
 * Use with LOCALE_TAGS for dates and numbers: now.toLocaleTimeString(LOCALE_TAGS[locale]).
 */
export function useLocale(): Locale {
  const rt = useRuntime();
  const [locale, setLocale] = useState(rt.locale());
  useEffect(() => rt.onLocale(setLocale), [rt]);
  return locale;
}

/**
 * The module's UI strings in the current language; re-renders when the language changes.
 *
 *   const messages = defineMessages({ en: { title: 'Lights', count: (n: number) => `${n} lights` }, tr: { title: 'Işıklar', count: (n: number) => `${n} lamba` } });
 *   const t = useT(messages);   // t.title, t.count(3)
 *
 * Keys missing in a language fall back to English. Define the catalog at module level, not inside the component.
 */
export function useT<M extends Messages>(messages: MessageCatalog<M>): M {
  return messages.get(useLocale());
}

/**
 * TV API access: call('tizen.x.y', ...args), bringToFront(), openApp(appId, opts).
 * In the preview isTV=false and call() rejects (except 'tizen.application.launch', which shows a toast).
 */
export function useTv() {
  const rt = useRuntime();
  return { isTV: rt.isTV, call: rt.tv, bringToFront: rt.bringToFront, openApp: rt.openApp };
}

/** Handle a command sent from the panel or the assistant (tv.command): useCommand('next', (data) => …). */
export function useCommand(name: string, handler: (data?: unknown) => void) {
  const rt = useRuntime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => rt.onCommand(name, (d) => ref.current(d)), [rt, name]);
}

/** Back key: if the handler returns true the key is consumed (e.g. close an open layer). Not listening while active=false. */
export function useBackHandler(handler: () => boolean, active = true) {
  const rt = useRuntime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => (active ? rt.onBack(() => ref.current()) : undefined), [rt, active]);
}

/** setInterval tied to the component's lifetime; pass null to pause. Always calls the latest fn. */
export function useInterval(fn: () => void, ms: number | null) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (ms == null) return;
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [ms]);
}

/** URL of a file in the module's assets/ folder: useAsset('images/logo.png'). */
export function useAsset(path: string): string {
  const rt = useRuntime();
  const w = useWidget();
  return rt.assetUrl(w.module, path);
}

/** State shared on the TV between widgets of the same module: const [v, setV] = useShared('index', 0). */
export function useShared<T>(key: string, initial: T): [T, (v: T) => void] {
  const rt = useRuntime();
  const w = useWidget();
  const [value, setValue] = useState<T>(() => (rt.getShared(w.module, key) as T | undefined) ?? initial);
  useEffect(() => rt.onShared(w.module, key, (v) => setValue(v as T)), [rt, w.module, key]);
  const set = useCallback((v: T) => rt.setShared(w.module, key, v), [rt, w.module, key]);
  return [value, set];
}

/** Current time as a Date, refreshed every intervalMs (default 10 s). */
export function useNow(intervalMs = 10_000): Date {
  const [now, setNow] = useState(() => new Date());
  useInterval(() => setNow(new Date()), intervalMs);
  return now;
}
